import { useNavigate } from "react-router-dom";
import Logo from "../assets/nav-logo.svg";
import HeroImg from "../assets/login-bg.jpg";
import { Recycle, Handshake, HandHeart, BadgeCheck } from "lucide-react";
import MailchimpForm from "@/components/MailchimpForm";
import { useEffect, useState } from "react";
import getRecentProducts from "@/scripts/getRecentProducts";

const Landing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState();
  const [recentProducts, setRecentProducts] = useState([]);

  const fetchRecentProducts = async () => {
    try {
      const cached = sessionStorage.getItem("recentProducts");
    
      if(cached) {
        setRecentProducts(JSON.parse(cached));
      } else {
        setLoading(true);
      }
      
      const data = await getRecentProducts();    
      console.log(data);  
      
      setRecentProducts(data);
      sessionStorage.setItem("recentProducts", JSON.stringify(data));
    } catch(err) {
      console.log("Error fetching data", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchRecentProducts();
  }, []);

  return (
    <div className="font-sans">
      {/* Navbar */}
      <nav className="px-0 md:px-6 py-4 bg-black shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div
            onClick={() => navigate("/")}
            className="flex justify-between items-center cursor-pointer"
          >
            <img src={Logo} alt="JP Associates" className="h-11 w-11 mr-1" />
            <div className="text-md font-bold text-green">JP Associates</div>
          </div>
          
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 bg-[#17be73] text-white text-sm rounded-md hover:bg-green-600 hover:text-white transition"
          >
            Sign in
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-[#f3f3f3] py-8 px-0 md:px-6 md:py-20">
        <div className="container mx-auto flex flex-col md:flex-row items-center">

          <div className="md:w-1/2 md:text-left mb-8 md:mb-0">
            <h1 className="max-w-xl text-3xl md:text-[2.6rem] font-bold text-[rgb(7,2,13)] mb-4 !leading-[1.2]">
              Your Premier Destination for
              <span className="text-[#17be73]"> Scrap Plastic Trading</span>
            </h1>

            <p className="max-w-md text-md text-[rgb(7,2,13)] mb-5 font-italic">
              Where plastic trading meets simplicity. Join us in shaping a sustainable future.
            </p>

            <button
              onClick={() => navigate("/login")}
              className="px-4 py-2 bg-[#17be73] text-white text-sm rounded-md hover:bg-green-600 hover:text-white transition"
            >
              Explore Products
            </button>
          </div>

          <div className="md:w-1/2 flex justify-center md:justify-end">
            <img
              src={HeroImg}
              alt="Scrap Plastic Trading"
              className="rounded-lg shadow-lg md:h-[300px]"
            />
          </div>
        </div>
      </section>

      {/* Combined Sections */}
      <section className="py-8 px-0 md:px-6 md:py-16 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            <div className="p-8 rounded-lg shadow-md">
              <div className="mb-3 flex justify-center">
                <Recycle size={40} color="#17be73" />
              </div>
              <h2 className="text-md md:text-lg font-semibold text-[rgb(7,2,13)] text-center mb-1">
                Wide Range of Materials
              </h2>
              <p className="text-md text-[rgb(7,2,13)] text-center leading-snug">
                Offering a comprehensive selection of recycled plastics, from
                PET to ABS, tailored to meet diverse industry needs.
              </p>
            </div>

            <div className="p-8 rounded-lg shadow-md">
              <div className="mb-3 flex justify-center">
                <HandHeart size={40} color="#17be73" />
              </div>
              <h2 className="text-md md:text-lg font-bold text-[rgb(7,2,13)] text-center mb-1">
                Environmental Commitment
              </h2>
              <p className="text-md text-[rgb(7,2,13)] text-center leading-snug">
                We understand the importance of environmental sustainability in
                today's world.
              </p>
            </div>

            <div className="p-8 rounded-lg shadow-md">
              <div className="mb-3 flex justify-center">
                <Handshake size={35} color="#17be73" />
              </div>
              <h2 className="text-md md:text-lg font-bold text-[rgb(7,2,13)] text-center mb-1">
                Customer Satisfaction
              </h2>
              <p className="text-md text-[rgb(7,2,13)] text-center leading-snug">
                Our dedicated team is committed to providing exceptional
                service, addressing your inquiries promptly.
              </p>
            </div>
            
          </div>
        </div>
      </section>

      {/* Recent Products */}
      <section className="py-8 px-0 md:px-6 md:py-16 bg-[#f3f3f3]">
        <div className="container mx-auto">
          <h2 className="text-lg md:text-xl font-bold text-[rgb(7,2,13)] mb-2 md:mb-6 text-center">
            Latest Products
          </h2>

          {/* Loading */}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="min-w-[300px] max-w-[300px] bg-white rounded-xl shadow-sm animate-pulse"
                >
                  <div className="w-full h-40 bg-gray-300 rounded-t-xl" />
                  <div className="p-3 space-y-2">
                    <div className="h-4 bg-gray-300 rounded w-2/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
          </div>

          {/* Carousel */}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
            {!loading && recentProducts.map((product) => (
              <div
                key={product.id}
                className="min-w-[300px] max-w-[300px] bg-white rounded-xl shadow-sm hover:shadow-md transition snap-start"
              >
                <div className="relative">
                  <img
                    src={product.url}
                    alt={product.grade}
                    className="w-full aspect-[16/9] object-cover rounded-t-xl"
                  />

                  <BadgeCheck 
                    size={24}
                    className="absolute top-2 right-2 text-white tex-green" 
                  />
                </div>

                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold truncate">
                      {product.grade || "Grade"}
                    </h3>

                    <span className="border border-black text-black text-xs px-2 py-1 rounded">
                      {product.type === "seller" ? "Buy" : "Sell"}
                    </span>
                  </div>
                  
                  <p className="text-sm">
                    {product.form || "Form"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About JP Associates */}
      <section className="py-8 px-0 md:px-6 md:py-16 bg-white">
        <div className="container">
          <h2 className="text-lg md:text-xl font-bold text-[rgb(7,2,13)] mb-2 text-center">
            About Us
          </h2>
          
          <p className="text-md text-[rgb(7,2,13)] text-center">
            At JP Associates, we stand at the forefront of the plastic
            recycling industry in India, extending our reach globally.
            Specializing in the trade and custom manufacturing of plastic
            materials, we offer a broad spectrum of products, including HDPE,
            LLDPE, LDPE, ABS, PBT, PP, PET, PC, PE, and PVC. Our commitment to
            quality and sustainability drives us to deliver high-quality
            reprocessed pellets tailored to meet our clients' specific needs.
            With a robust network and a dedicated team, JP Associates is your
            trusted partner in sustainable plastic solutions.
          </p>
        </div>
      </section>

      {/* Join Our Community */}
      <section className="py-8 px-0 md:px-6 md:py-16 bg-[#f3f3f3]">
        <div className="container">
          <h2 className="text-lg md:text-xl font-bold text-[rgb(7,2,13)] mb-2 text-center">
            Join Our Community
          </h2>
          
          <p className="text-md text-[rgb(7,2,13)] text-center">
            Join hands with thousands of passionate individuals committed to
            preserving our planet. Explore sustainable solutions, connect with
            like-minded enthusiasts, and be part of the change. Together, we
            can create a greener, healthier world for generations to come.
          </p>
        </div>
      </section>

      {/* Contact Us */}
      <section className="py-8 px-0 md:px-6 md:py-16 bg-white">
        <div className="container">
          <h2 className="text-lg md:text-xl font-bold text-[rgb(7,2,13)] mb-2 text-center">
            Contact Us
          </h2>
          <p className="text-md text-[rgb(7,2,13)] mb-4 md:mb-6 text-center">
            We are here to help you! If you have any questions or need
            assistance, please fill out the form below. <br />We'll get back to you as
            soon as possible.
          </p>
          <div className="mx-auto max-w-2xl space-y-4">
            <MailchimpForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-0 md:px-6 md:py-16 bg-[rgb(7,2,13)] text-white text-center text-[15px]">
        <p className="mb-3">© 2026 JP Associates. All rights reserved.</p>

        <div className="space-x-4">
          <a href="/terms-of-use" className="hover:underline">
            Terms of Use
          </a>
          <a href="/privacy-policy" className="hover:underline">
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
